import React, { useState, useEffect, useMemo } from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, type Plan } from '@/lib/api';
import { FEATURED_PLAN_ID, PUBLISHED_PLANS, pricingTiers, toPlans } from '@/lib/plans';
import { SALES_CONTACT_HREF } from '@/lib/contact';
import { usePageSEO } from '@/hooks/usePageSEO';
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';
import { pageMetadata } from '@/lib/seo/metadata';
import { useJsonLd } from '@/hooks/useJsonLd';
import { softwareApplicationJsonLd } from '@/lib/seo/structured-data';
import { PublicContainer } from '@/components/layouts/PublicContainer';
import { useCtaTracker } from '@/hooks/useCtaTracker';

const Pricing: React.FC = () => {
  const seo = pageMetadata(PUBLIC_PAGES.pricing);
  usePageSEO(seo);

  const trackCta = useCtaTracker();
  const [livePlans, setLivePlans] = useState<Plan[]>([]);
  const tiers = useMemo(() => pricingTiers(PUBLISHED_PLANS, livePlans), [livePlans]);

  // Sourced from the tiers rendered below, so the structured data can never
  // claim a price the page does not show. Custom tiers carry no price and are
  // omitted rather than given a fabricated number.
  const pricingJsonLd = useMemo(() => {
    const offers = tiers
      .filter((tier): tier is typeof tier & { price: number } => tier.price !== null)
      .map((tier) => ({ name: tier.name, price: tier.price, priceCurrency: tier.currency }));
    if (offers.length === 0) return null;
    return softwareApplicationJsonLd({
      url: seo.canonical,
      description: seo.description,
      offers,
    });
  }, [tiers, seo.canonical, seo.description]);
  useJsonLd('pricing', pricingJsonLd);

  useEffect(() => {
    let active = true;
    api
      .getPlans()
      .then((plans) => {
        // Validated rather than trusted: see toPlans.
        if (active) setLivePlans(toPlans(plans));
      })
      // The published prices are already on screen; a failed refresh leaves them.
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="relative w-full min-h-screen py-24">
      <PublicContainer className="text-center">
        <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">
          Simple, transparent pricing.
        </h1>
        <p className="mt-6 text-lg leading-8 text-muted-foreground">
          Choose the plan that fits your team's velocity.
        </p>

        {/* Cards */}
        <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-2 xl:grid-cols-4">
          {tiers.map((tier) => (
            <div key={tier.id} className={`rounded-2xl p-8 border xl:p-10 flex flex-col justify-between transition-all ${tier.id === FEATURED_PLAN_ID ? 'bg-surface-2 border-lime/60 transform scale-105 shadow-2xl shadow-lime/5' : 'bg-surface-1 border-hairline hover:bg-surface-2'}`}>
              <div>
                <h3 className="text-lg font-semibold leading-8 text-foreground">{tier.name}</h3>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">{tier.blurb}</p>
                <p className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-4xl font-semibold tracking-tight text-foreground">
                    {tier.price === null ? 'Custom' : `$${tier.price}`}
                  </span>
                  {tier.price !== null && <span className="mono text-sm leading-6 text-muted-foreground">/{tier.interval}</span>}
                </p>
                <ul className="mt-8 space-y-3 text-sm leading-6 text-left text-muted-foreground">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex gap-x-3">
                      <Check className="h-6 w-5 flex-none text-lime" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              {(() => {
                const ctaClassName = `mt-8 block rounded-md px-3 py-2.5 text-center text-sm font-medium leading-6 transition-all ${tier.id === FEATURED_PLAN_ID
                  ? 'bg-lime text-lime-foreground hover:brightness-105 active:scale-[0.98]'
                  : 'border border-hairline text-foreground hover:bg-surface-2'
                  }`;

                // A custom tier has nothing to subscribe to, so it opens a mail
                // client rather than linking to a page that does not exist.
                // The featured tier is the page's primary call to action; the
                // rest offer an alternative path, so they report as secondary.
                const priority = tier.id === FEATURED_PLAN_ID ? 'primary' : 'secondary';

                return tier.price === null ? (
                  <a
                    href={SALES_CONTACT_HREF}
                    className={ctaClassName}
                    onClick={() => trackCta({ action: 'contact_sales', placement: 'pricing_card', priority })}
                  >
                    Contact Sales
                  </a>
                ) : (
                  <Link
                    to="/signup"
                    className={ctaClassName}
                    onClick={() => trackCta({ action: 'signup', placement: 'pricing_card', priority })}
                  >
                    {tier.price === 0 ? 'Get Started' : 'Subscribe Now'}
                  </Link>
                );
              })()}
            </div>
          ))}
        </div>
      </PublicContainer>
    </div>
  );
};

export default Pricing;
